import { z } from 'zod'
import { etaConfidenceLabelSchema } from './eta.js'
import { paginatedSchema, paginationQuerySchema } from './pagination.js'

const isoDateTime = z.iso.datetime({ offset: true })
const sourceSchema = z.enum(['egis', 'kestrel'])

/** Which source located the officer on a job: the groups the job list is filtered by. */
export const ETA_COVERAGE_CATEGORIES = [
  'both',
  'chase_only',
  'kestrel_only',
  'neither',
  'kestrel_unavailable',
  'kestrel_not_compared',
] as const
export const etaCoverageCategorySchema = z.enum(ETA_COVERAGE_CATEGORIES)
export type EtaCoverageCategory = z.infer<typeof etaCoverageCategorySchema>
export const etaCoverageFilterSchema = z.enum(['all', ...ETA_COVERAGE_CATEGORIES])
export type EtaCoverageFilter = z.infer<typeof etaCoverageFilterSchema>

export const etaSourceComparisonQuerySchema = z.object({
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  filter: etaCoverageFilterSchema.default('all'),
  ...paginationQuerySchema.shape,
})
export type EtaSourceComparisonQuery = z.input<typeof etaSourceComparisonQuerySchema>

/** Whether a source located the officer, did not, or could not be read at all. */
export const etaPointStateSchema = z.enum(['located', 'not_located', 'unavailable'])
export type EtaPointState = z.infer<typeof etaPointStateSchema>

/** A source's mark on a job: its point states summed up, or not compared when it has no points (Kestrel was off). */
export const etaCoverageMarkSchema = z.enum(['located', 'not_located', 'unavailable', 'not_compared'])
export type EtaCoverageMark = z.infer<typeof etaCoverageMarkSchema>

const trendPointSchema = z.object({ at: z.string(), score: z.number() })

export const etaCoverageSourceSchema = z.object({
  source: sourceSchema,
  mark: etaCoverageMarkSchema,
  averageConfidence: z.number().nullable(),
  timeToFirstEtaSeconds: z.number().int().nullable(),
  trend: z.array(trendPointSchema),
})

export const etaCoverageJobSchema = z.object({
  jobNumber: z.string(),
  officerName: z.string().nullable(),
  allocatedAt: z.string(),
  arrivedAt: z.string().nullable(),
  lastAskedAt: z.string(),
  category: etaCoverageCategorySchema,
  sources: z.array(etaCoverageSourceSchema),
})
export type EtaCoverageJob = z.infer<typeof etaCoverageJobSchema>

const sourceTotalsSchema = z.object({
  located: z.number().int(),
  /** Jobs the source was asked about at all: the denominator for its located share. */
  compared: z.number().int(),
  averageConfidence: z.number().nullable(),
  medianTimeToFirstEtaSeconds: z.number().int().nullable(),
})

export const etaCoverageHeadlineSchema = z.object({
  jobs: z.number().int(),
  egis: sourceTotalsSchema,
  kestrel: sourceTotalsSchema,
  kestrelOnly: z.number().int(),
  neither: z.number().int(),
  kestrelUnavailable: z.number().int(),
  kestrelNotCompared: z.number().int(),
  closerAt15: z.object({ jobs: z.number().int(), kestrelCloser: z.number().int(), egisCloser: z.number().int() }),
})
export type EtaCoverageHeadline = z.infer<typeof etaCoverageHeadlineSchema>

export const etaSourceComparisonSchema = z.object({
  from: z.string(),
  to: z.string(),
  headline: etaCoverageHeadlineSchema,
  counts: z.record(etaCoverageFilterSchema, z.number().int()),
  jobs: paginatedSchema(etaCoverageJobSchema),
})
export type EtaSourceComparison = z.infer<typeof etaSourceComparisonSchema>

export const etaCoverageTimelineSchema = z.object({
  jobNumber: z.string(),
  officerName: z.string().nullable(),
  allocatedAt: z.string().nullable(),
  arrivedAt: z.string().nullable(),
  firstEta: z.object({ egis: z.string().nullable(), kestrel: z.string().nullable() }),
  points: z.array(
    z.object({
      source: sourceSchema,
      at: z.string(),
      state: etaPointStateSchema,
      confidence: etaConfidenceLabelSchema.nullable(),
      score: z.number().nullable(),
    }),
  ),
})
export type EtaCoverageTimeline = z.infer<typeof etaCoverageTimelineSchema>
